import { find, values } from "lodash";
import React, { useEffect, useMemo } from "react";
import { matchPath, RouteComponentProps } from "react-router-dom";
import WizardPage from "../../../../components/WizardPage";
import { useWallets } from "../../../../context/wallets";
import AlreadyPendingStep from "./AlreadyPendingStep";
import ConfirmWalletStep from "./ConfirmWalletStep";
import DonateStep from "./DonateStep";
import SetDonationStep from "./SetDonationStep";
import SummaryStep from "./SummaryStep";
import WalletHotspotsSection from "./WalletHotspotsSection";

interface Props
  extends RouteComponentProps<{
    walletAddress: string;
    stepKey: string;
  }> {}

const VerifyWalletWizardPage: React.FC<Props> = ({
  history,
  location: { pathname }
}) => {
  const { fetchWallets, wallets, getActiveWalletVerification } = useWallets();

  const wizardPageMatch = matchPath<{
    walletAddress: string;
    stepKey: string;
  }>(pathname, {
    path: "/workspace/wallets/verify/:walletAddress/:stepKey"
  });

  const currentWallet = useMemo(
    () =>
      find(values(wallets), {
        address: wizardPageMatch?.params.walletAddress
      }),
    [wallets, wizardPageMatch?.params.walletAddress]
  );

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  useEffect(() => {
    if (!currentWallet) return;

    if (currentWallet.current_verification_status === "completed") {
      history.replace("/workspace/wallets");
      return;
    }

    if (currentWallet.current_verification_status === "pending") {
      const activeVerification = getActiveWalletVerification(
        currentWallet.address
      ); // This will exist if the wallet is being verified already by the current workspace

      if (
        !activeVerification &&
        wizardPageMatch?.params.stepKey !== "already-pending"
      ) {
        history.replace(
          `/workspace/wallets/verify/${currentWallet.address}/already-pending`
        );
      } else if (
        activeVerification &&
        !["donate", "summary"].includes(
          wizardPageMatch?.params.stepKey as string
        )
      ) {
        history.replace(
          `/workspace/wallets/verify/${currentWallet.address}/donate`
        );
      }
    }
  }, [
    currentWallet,
    getActiveWalletVerification,
    history,
    wizardPageMatch?.params.stepKey
  ]);

  const wizardSteps = useMemo(
    () =>
      currentWallet?.current_verification_status === "pending" &&
      !getActiveWalletVerification(currentWallet.address)
        ? alreadyPendingWizardSteps
        : unverifiedWalletWizardSteps,
    [
      currentWallet?.address,
      currentWallet?.current_verification_status,
      getActiveWalletVerification
    ]
  );

  const renderWalletsSection = useMemo(
    () => (
      <WalletHotspotsSection
        walletAddress={wizardPageMatch?.params.walletAddress as string}
      />
    ),
    [wizardPageMatch?.params.walletAddress]
  );

  return (
    <WizardPage
      name="Wallet verification process"
      basePath={`/workspace/wallets/verify/${wizardPageMatch?.params.walletAddress}`}
      steps={wizardSteps}
      renderExtraSection={renderWalletsSection}
    />
  );
};

export default VerifyWalletWizardPage;

//
// Utils
//

const unverifiedWalletWizardSteps = [
  {
    key: "confirm-wallet",
    title: "Confirm wallet",
    component: ConfirmWalletStep
  },
  {
    key: "set-donation",
    title: "Set donation",
    component: SetDonationStep
  },
  {
    key: "donate",
    title: "Donate",
    component: DonateStep
  },
  {
    key: "summary",
    title: "Donation pending - thank you",
    component: SummaryStep
  }
];

const alreadyPendingWizardSteps = [
  {
    key: "already-pending",
    title: "Already pending",
    component: AlreadyPendingStep
  }
];
